import React, { useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Stack, Button } from '@mui/material';
import { AddPhoto } from '../elements/AddPhoto';
import { RoomLevelIssueItem } from './RoomLevelIssues';
import { CustomAttributeItem } from './ModalCustomAttributes';
import { WallItem } from './AddMultiPhoto';
import { AdditionalStructureItem } from './AdditionalStructureItem';
import { FormDetailEnd } from './FormDetailEnd';
import { dropdownValueTypes } from './ModalStartInspection';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { useParams } from 'react-router-dom';
import { entriesInclude } from 'utils/entries';

const defaultRoomLevel = { issuesType: '', desc: '', photo: null };
const defaultcustomAttributes = { content: '', desc: '', photo: null };

type StepLocationDetailProps = {
  roomDetail?: { id?: number; name?: string };
};

export const StepLocationDetail = ({ roomDetail }: StepLocationDetailProps) => {
  const { control, setValue } = useFormContext();
  const { clientId } = useParams();
  const { systemDropdownValues, singleEntryField, listOption } = useFilteredRoomContext(clientId);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'locationDetail.roomLevelIssues'
  });
  const {
    fields: customFields,
    append: appendCustom,
    remove: removeCustom
  } = useFieldArray({ control, name: 'locationDetail.customAttributes' });
  const {
    fields: wallFields,
    append: appendWall,
    remove: removeWall,
    update: updateWall
  } = useFieldArray({ control, name: 'locationDetail.walls' });
  const {
    fields: addFields,
    append: appendAdd,
    remove: removeAdd
  } = useFieldArray({ control, name: 'locationDetail.additionalStructure' });

  const roomLevelIssueOption = useMemo(() => {
    return systemDropdownValues?.filter((item) =>
      item.options.some((opt) => opt.type === dropdownValueTypes.roomLevelIssue)
    );
  }, [systemDropdownValues]);

  const roomLevelIssueOptionSingleField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.roomLevelIssue)
    );
  }, [singleEntryField]);

  const additionalStructureOption = useMemo(() => {
    return systemDropdownValues?.filter((item) =>
      item.options.some((opt) => opt.type === dropdownValueTypes.additionalStructure)
    );
  }, [systemDropdownValues]);

  const additionalStructureOptionSingleField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.additionalStructure)
    );
  }, [singleEntryField]);

  const roomOption = useMemo(() => {
    return systemDropdownValues?.filter((item) =>
      item.options.some((opt) => opt.type === dropdownValueTypes.room)
    );
  }, [systemDropdownValues]);

  const roomSingleField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.room)
    );
  }, [singleEntryField]);

  const complianceOption = useMemo(() => {
    return listOption?.users?.map((u) => ({ label: u.name, value: u.name })) || [];
  }, [listOption?.users]);

  return (
    <>
      <Stack flexDirection={'row'} gap={'20px'}>
        <Controller
          control={control}
          name={`locationDetail.outSidePhoto`}
          render={({ field: { value }, fieldState }) => (
            <AddPhoto
              errorText={fieldState.error?.message || ''}
              label={'Outside Photo(s):'}
              value={value}
              onChange={(file) => file && setValue('locationDetail.outSidePhoto', file)}
            />
          )}
        />
        <Controller
          control={control}
          name={`locationDetail.internalPhoto`}
          render={({ field: { value }, fieldState }) => (
            <AddPhoto
              errorText={fieldState?.error?.message || ''}
              label={'Internal Panoramic Photo:'}
              value={value}
              onChange={(file) => file && setValue('locationDetail.internalPhoto', file)}
            />
          )}
        />
      </Stack>
      <Stack width={'100%'} alignItems={'flex-end'}>
        {fields.map((field, index) => (
          <RoomLevelIssueItem
            key={field.id}
            canRemove={fields.length > 0}
            index={index}
            remove={remove}
            singleEntryField={roomLevelIssueOptionSingleField || []}
            issueTypeOptions={roomLevelIssueOption || []}
          />
        ))}
        <Button variant="contained" onClick={() => append(defaultRoomLevel)}>
          Add Another Issue
        </Button>
      </Stack>

      <Stack width={'100%'} alignItems={'flex-end'}>
        {customFields.map((field, index) => (
          <CustomAttributeItem key={field.id} index={index} remove={removeCustom} />
        ))}
        <Button variant="contained" onClick={() => appendCustom(defaultcustomAttributes)}>
          Add Custom Attribute
        </Button>
      </Stack>

      <Stack width={'100%'} alignItems={'flex-end'}>
        {wallFields.map((field, index) => (
          <WallItem
            key={field.id}
            index={index}
            onRemove={() => removeWall(index)}
            onAddPhoto={(newFiles) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const current = (field as any)?.photos || [];
              updateWall(index, { photos: [...current, ...newFiles] });
            }}
          />
        ))}
        <Button variant="contained" onClick={() => appendWall({ photos: [] })}>
          Add Another Wall
        </Button>
      </Stack>

      <Stack width={'100%'} alignItems={'flex-end'}>
        {addFields.map((field, index) => (
          <AdditionalStructureItem
            key={field.id}
            index={index}
            remove={() => removeAdd(index)}
            titleOptions={
              additionalStructureOption?.length
                ? additionalStructureOption[0]?.dropdownValues?.map((opt) => ({
                    label: opt.value,
                    value: opt.id.toString()
                  }))
                : []
            }
            additionalStructureOptionSingleField={additionalStructureOptionSingleField || []}
          />
        ))}
        <Button variant="contained" onClick={() => appendAdd({ title: '', photos: [] })}>
          Add Additional Structure
        </Button>
      </Stack>

      <FormDetailEnd
        roomSingleField={roomSingleField || []}
        roomDetailName={roomDetail?.name || ''}
        roomOptions={roomOption || []}
        complianceOption={complianceOption}
      />
    </>
  );
};
